import * as t from 'io-ts';

import {AccountAttributeUpdateActionsEnum} from '../../../../helpers/models/accountAttributeUpdateHelpers';
import {CardAttributeUpdateActionsEnum} from '../../../../helpers/models/cardAttributeUpdateHelpers';
import {CardNoteUpdateActionsEnum} from '../../../../helpers/models/cardNoteHelpers';
import {makeEnumRuntimeType, RBannerStateUidRef} from '../../../../models/api/apiTypes';
import {RCard} from '../../../../models/api/cardModel';
import {RCardNote} from '../../../../models/api/cardNoteModel';
import {RConversation, RConversationBase} from '../../../../models/api/conversationModel';
import {RCustomField} from '../../../../models/api/customFieldModel';
import {RCellsContext} from '../../cells/context/cellsContext';
import {RContactDetailsPanelId} from '../../panelStateBase';

/*
 * Runtime types.
 */

export enum BannerTypesEnum {
  INFO = 'INFO',
  UNDO = 'UNDO',
  ERROR = 'ERROR',
  SUCCESS = 'SUCCESS',
}

const RBannerStateBase = t.readonly(
  t.intersection([
    t.type({
      type: makeEnumRuntimeType(BannerTypesEnum),
      uid: t.string,
      createdAt: t.number,
    }),
    t.partial({
      expiresAt: t.number,
    }),
  ]),
);

/*
 * Undo banners.
 */

export enum UndoBannerTypesEnum {
  CONVERSATION = 'CONVERSATION',
  MESSAGE = 'MESSAGE',
  CONTACT_DETAILS = 'CONTACT_DETAILS',
  ACCOUNT_DETAILS = 'ACCOUNT_DETAILS',
}

export enum ConversationUndoBannerTypesEnum {
  ASSIGNEE = 'ASSIGNEE',
  REMINDER = 'REMINDER',
  STAGE = 'STAGE',
  STATUS = 'STATUS',
  TAG = 'TAG',
  MOVE = 'MOVE',
}

const RConversationUndoBannerState = t.intersection([
  RBannerStateBase,
  t.type({
    type: t.literal(BannerTypesEnum.UNDO),
    undoType: t.literal(UndoBannerTypesEnum.CONVERSATION),
    conversationUndoType: makeEnumRuntimeType(ConversationUndoBannerTypesEnum),
    cellsContext: RCellsContext,
    oldConversations: t.readonlyArray(RConversationBase),
    newConversations: t.readonlyArray(RConversationBase),
    onlyOldConversation: t.union([t.undefined, RConversation]),
  }),
]);

export enum MessageUndoBannerTypesEnum {
  ONLINE = 'ONLINE',
  OFFLINE = 'OFFLINE',
}

const RMessageUndoBannerState = t.intersection([
  RBannerStateBase,
  t.type({
    type: t.literal(BannerTypesEnum.UNDO),
    undoType: t.literal(UndoBannerTypesEnum.MESSAGE),
    messageUndoType: makeEnumRuntimeType(MessageUndoBannerTypesEnum),
    messageUid: t.string,
    expiresAt: t.number,
  }),
]);

const RContactDetailsCardAttributeAction = t.type({
  type: makeEnumRuntimeType(CardAttributeUpdateActionsEnum),
  payload: t.record(t.string, t.union([t.string, t.number, RCustomField])),
});

const RContactDetailsCardNoteAction = t.type({
  type: makeEnumRuntimeType(CardNoteUpdateActionsEnum),
  payload: t.record(t.string, t.union([t.string, t.number, RCardNote])),
});

const RContactDetailsUndoBannerState = t.intersection([
  RBannerStateBase,
  t.type({
    type: t.literal(BannerTypesEnum.UNDO),
    undoType: t.literal(UndoBannerTypesEnum.CONTACT_DETAILS),
    // We need the contact details panel ID in case the undo results in a
    // conflict and the user wants to resolve that conflict and preview a merge.
    panelId: RContactDetailsPanelId,
    cardId: t.number,
    action: t.union([RContactDetailsCardAttributeAction, RContactDetailsCardNoteAction]),
  }),
]);

const RAccountAttributeUpdateAction = t.type({
  type: makeEnumRuntimeType(AccountAttributeUpdateActionsEnum),
  payload: t.record(t.string, t.union([t.string, t.number, t.readonlyArray(t.string), RCustomField])),
});

const RAccountDetailsUndoBannerState = t.intersection([
  RBannerStateBase,
  t.type({
    type: t.literal(BannerTypesEnum.UNDO),
    undoType: t.literal(UndoBannerTypesEnum.ACCOUNT_DETAILS),
    accountId: t.number,
    action: RAccountAttributeUpdateAction,
  }),
]);

const RUndoBannerState = t.intersection(
  [
    t.type({
      type: t.literal(BannerTypesEnum.UNDO),
    }),
    t.taggedUnion('undoType', [
      RConversationUndoBannerState,
      RMessageUndoBannerState,
      RContactDetailsUndoBannerState,
      RAccountDetailsUndoBannerState,
    ]),
  ],
  'undoBannerState',
);

/*
 * Error Banners.
 */

export enum ErrorBannerTypesEnum {
  CONTACT_DETAILS = 'CONTACT_DETAILS',
  ACCOUNTS = 'ACCOUNTS',
  CONTACTS_MANAGER = 'CONTACTS_MANAGER',
}

export enum ContactDetailsErrorTypesEnum {
  MERGE_FAILED = 'MERGE_FAILED',
  MERGE_PREVIEW_FAILED = 'MERGE_PREVIEW_FAILED',
  CREATION_FAILED = 'CREATION_FAILED',
  DELETION_FAILED = 'DELETION_FAILED',
  HANDLE_TAKEN = 'HANDLE_TAKEN',
  // TODO: Show error banner when a detail updated event fails
  DETAIL_UPDATE_FAILED = 'DETAIL_UPDATE_FAILED',
  CONVERT_PRIVATE_CONTACT_FAILED = 'CONVERT_PRIVATE_CONTACT_FAILED',
  TOO_MANY_HANDLES = 'TOO_MANY_HANDLES',
}

const RContactDetailsErrorBannerState = t.intersection([
  RBannerStateBase,
  t.type({
    type: t.literal(BannerTypesEnum.ERROR),
    errorType: t.literal(ErrorBannerTypesEnum.CONTACT_DETAILS),
    contactDetailsErrorType: makeEnumRuntimeType(ContactDetailsErrorTypesEnum),
    expiresAt: t.number,
    params: t.union([
      t.partial({
        mainCard: t.union([RCard, t.undefined]),
        conflictingContactUid: t.union([t.string, t.undefined]),
        conflictingCard: t.union([RCard, t.undefined]),
        contactDetailsPanelId: t.union([RContactDetailsPanelId, t.undefined]),
      }),
      t.undefined,
    ]),
  }),
]);

export enum AccountsErrorBannerTypesEnum {
  CREATION_FAILED = 'CREATION_FAILED',
  DELETION_FAILED = 'DELETION_FAILED',
  DOMAIN_UPDATE_FAILED = 'DOMAIN_UPDATE_FAILED',
  DOMAIN_REMOVAL_FAILED = 'DOMAIN_REMOVAL_FAILED',
}

const RAccountsErrorBannerState = t.intersection([
  RBannerStateBase,
  t.type({
    type: t.literal(BannerTypesEnum.ERROR),
    errorType: t.literal(ErrorBannerTypesEnum.ACCOUNTS),
    accountsErrorType: makeEnumRuntimeType(AccountsErrorBannerTypesEnum),
    expiresAt: t.number,
  }),
]);

export enum ContactsManagerErrorBannerTypesEnum {
  MERGE_FAILED = 'MERGE_FAILED',
  CONTACT_CREATE_FAILED = 'CONTACT_CREATE_FAILED',
}

const RContactsManagerErrorBannerState = t.intersection([
  RBannerStateBase,
  t.type({
    type: t.literal(BannerTypesEnum.ERROR),
    errorType: t.literal(ErrorBannerTypesEnum.CONTACTS_MANAGER),
    contactsManagerErrorType: makeEnumRuntimeType(ContactsManagerErrorBannerTypesEnum),
    expiresAt: t.number,
  }),
]);

const RErrorBannerState = t.intersection(
  [
    t.type({
      type: t.literal(BannerTypesEnum.ERROR),
    }),
    t.taggedUnion('errorType', [
      RContactDetailsErrorBannerState,
      RAccountsErrorBannerState,
      RContactsManagerErrorBannerState,
    ]),
  ],
  'errorBannerState',
);

/*
 * Success Banners.
 */

export enum SuccessBannerTypesEnum {
  CONTACTS_MANAGER = 'CONTACTS_MANAGER_SUCCESS',
}

export enum ContactsManagerSuccessBannerTypesEnum {
  CARDS_MERGED = 'CARDS_MERGED',
  CONTACT_CREATED = 'CONTACT_CREATED',
}

const RContactsManagerSuccessBannerStateBase = t.intersection([
  RBannerStateBase,
  t.type({
    type: t.literal(BannerTypesEnum.SUCCESS),
    successType: t.literal(SuccessBannerTypesEnum.CONTACTS_MANAGER),
    contactsManagerSuccessType: makeEnumRuntimeType(ContactsManagerSuccessBannerTypesEnum),
    expiresAt: t.number,
  }),
]);

const RContactsManagerCardsMergedSuccessBannerState = t.intersection([
  RContactsManagerSuccessBannerStateBase,
  t.type({
    contactsManagerSuccessType: t.literal(ContactsManagerSuccessBannerTypesEnum.CARDS_MERGED),
    totalCards: t.number,
  }),
]);

const RContactsManagerContactCreatedSuccessBannerState = t.intersection([
  RContactsManagerSuccessBannerStateBase,
  t.type({
    contactsManagerSuccessType: t.literal(ContactsManagerSuccessBannerTypesEnum.CONTACT_CREATED),
  }),
]);

const RContactsManagerSuccessBannerState = t.taggedUnion('contactsManagerType', [
  RContactsManagerCardsMergedSuccessBannerState,
  RContactsManagerContactCreatedSuccessBannerState,
]);

const RSuccessBannerState = t.intersection(
  [
    t.type({
      type: t.literal(BannerTypesEnum.SUCCESS),
    }),
    RContactsManagerSuccessBannerState,
  ],
  'successBannerState',
);

/*
 * Info banners.
 */

export enum InfoBannerTypesEnum {
  ACCOUNTS = 'ACCOUNTS',
  APPLICATIONS = 'APPLICATIONS',
}

export enum AccountsInfoBannerTypesEnum {
  ACCOUNT_CREATED = 'ACCOUNT_CREATED',
  ACCOUNT_DELETED = 'ACCOUNT_DELETED',
  DOMAINS_UPDATED = 'DOMAINS_UPDATED',
  DOMAINS_REMOVED = 'DOMAINS_REMOVED',
}

const RAccountsInfoBannerState = t.intersection([
  RBannerStateBase,
  t.type({
    type: t.literal(BannerTypesEnum.INFO),
    infoType: t.literal(InfoBannerTypesEnum.ACCOUNTS),
    accountsInfoType: makeEnumRuntimeType(AccountsInfoBannerTypesEnum),
    expiresAt: t.number,
  }),
]);

export enum ApplicationBannerTypesEnum {
  APPLICATION_ENABLED = 'APPLICATION_ENABLED',
}

const RApplicationBannerState = t.intersection([
  RBannerStateBase,
  t.type({
    type: t.literal(BannerTypesEnum.INFO),
    infoType: t.literal(InfoBannerTypesEnum.APPLICATIONS),
    applicationType: makeEnumRuntimeType(ApplicationBannerTypesEnum),
  }),
  t.partial({
    integrationId: t.number,
    legacyApplicationId: t.number,
  }),
]);

const RInfoBannerState = t.intersection(
  [
    t.type({
      type: t.literal(BannerTypesEnum.INFO),
    }),
    t.taggedUnion('infoType', [RAccountsInfoBannerState, RApplicationBannerState]),
  ],
  'infoBannerState',
);

/*
 * All banners.
 */

export const RBannerState = t.taggedUnion(
  'type',
  [RUndoBannerState, RInfoBannerState, RErrorBannerState, RSuccessBannerState],
  'bannerState',
);
RBannerStateUidRef.setTargetType(RBannerState);

/*
 * Types.
 */

export interface ConversationUndoBannerState
  extends Readonly<t.TypeOf<typeof RConversationUndoBannerState>> {}
export interface MessageUndoBannerState extends Readonly<t.TypeOf<typeof RMessageUndoBannerState>> {}

export interface ContactDetailsUndoBannerState
  extends Readonly<t.TypeOf<typeof RContactDetailsUndoBannerState>> {}
export interface AccountDetailsUndoBannerState
  extends Readonly<t.TypeOf<typeof RAccountDetailsUndoBannerState>> {}

export interface ContactDetailsErrorBannerState
  extends Readonly<t.TypeOf<typeof RContactDetailsErrorBannerState>> {}
export interface AccountsErrorBannerState extends Readonly<t.TypeOf<typeof RAccountsErrorBannerState>> {}
export interface ContactsManagerErrorBannerState
  extends Readonly<t.TypeOf<typeof RContactsManagerErrorBannerState>> {}

export interface ContactsManagerCardsMergedSuccessBannerState
  extends Readonly<t.TypeOf<typeof RContactsManagerCardsMergedSuccessBannerState>> {}
export interface ContactsManagerContactCreatedSuccessBannerState
  extends Readonly<t.TypeOf<typeof RContactsManagerContactCreatedSuccessBannerState>> {}

export type ContactsManagerSuccessBannerState =
  | ContactsManagerCardsMergedSuccessBannerState
  | ContactsManagerContactCreatedSuccessBannerState;

export type ContactsManagerBannerState = ContactsManagerSuccessBannerState | ContactsManagerErrorBannerState;

export interface AccountsInfoBannerState extends Readonly<t.TypeOf<typeof RAccountsInfoBannerState>> {}

export interface ApplicationBannerState extends Readonly<t.TypeOf<typeof RApplicationBannerState>> {}

export type UndoBannerState =
  | ConversationUndoBannerState
  | MessageUndoBannerState
  | ContactDetailsUndoBannerState
  | AccountDetailsUndoBannerState;

type InfoBannerState = AccountsInfoBannerState | ApplicationBannerState;

export type ErrorBannerState =
  | ContactDetailsErrorBannerState
  | AccountsErrorBannerState
  | ContactsManagerErrorBannerState;

type SuccessBannerState = ContactsManagerSuccessBannerState;

export type BannerState = UndoBannerState | InfoBannerState | ErrorBannerState | SuccessBannerState;
